import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Building2,
  BadgePercent,
  FileCheck,
  ShieldCheck,
  Lock,
  Headphones,
  Truck,
  Wrench,
  HardHat,
  Zap,
  Users,
  Clock,
  Camera,
  BatteryCharging,
  Lightbulb,
  Phone,
  ChevronRight,
} from "lucide-react";

export const Route = createFileRoute("/zakelijk")({
  head: () => ({
    meta: [
      { title: "Zakelijk B2B | Elvion Group" },
      {
        name: "description",
        content:
          "B2B-partner voor elektrotechniek, laadpalen, camerabewaking, netwerk en LED. Eigen accountmanager en landelijke service.",
      },
      { property: "og:title", content: "Zakelijk B2B | Elvion Group" },
      {
        property: "og:description",
        content:
          "B2B-partner voor elektrotechniek, laadpalen, camerabewaking, netwerk en LED. Eigen accountmanager en landelijke service.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://elvion-group.nl/zakelijk" },
      { property: "og:site_name", content: "Elvion Group Nederland" },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Zakelijk B2B | Elvion Group" },
      {
        name: "twitter:description",
        content:
          "B2B-partner voor elektrotechniek, laadpalen, camerabewaking en netwerk. Eigen accountmanager en landelijke service.",
      },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/zakelijk" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Werkt Elvion Group Nederland in heel Nederland?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. Onze monteurs werken vanuit meerdere regio's en voeren projecten en serviceopdrachten in heel Nederland uit.",
              },
            },
            {
              "@type": "Question",
              name: "Kan ik een raamovereenkomst afsluiten?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. Voor terugkerende opdrachten stellen we een raamovereenkomst op met vaste tarieven, responstijden en servicecondities.",
              },
            },
            {
              "@type": "Question",
              name: "Zijn de installaties NEN-gecertificeerd?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Alle werkzaamheden voeren wij uit conform NEN 1010 (nieuwe installaties) en NEN 3140 (periodieke inspectie van elektrische arbeidsmiddelen).",
              },
            },
            {
              "@type": "Question",
              name: "Hoe snel kan een storing worden opgepakt?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Klanten met een servicecontract profiteren van vaste responstijden, afhankelijk van het gekozen serviceniveau.",
              },
            },
          ],
        }),
      },
    ],

  }),
  component: ZakelijkPage,
});

const features = [
  {
    icon: BadgePercent,
    title: "Inkoopprijzen",
    body: "Exclusieve zakelijke prijzen op A-merken zoals AEG, Hikvision, Dahua en meer. Hoe meer u afneemt, hoe voordeliger.",
  },
  {
    icon: FileCheck,
    title: "Digitaal offertes & orders",
    body: "Offertes digitaal ontvangen, ondertekenen en direct omzetten naar bestelling. Alles overzichtelijk in uw klantportaal.",
  },
  {
    icon: ShieldCheck,
    title: "Eigen accountmanager",
    body: "Een vaste contactpersoon die uw projecten en voorkeuren kent. Geen wachttijden, geen herhaalvragen.",
  },
  {
    icon: Lock,
    title: "2FA-beveiligd portaal",
    body: "Uw offertes, facturen en projectdocumentatie zijn veilig opgeslagen in een met twee-factor-authenticatie beveiligd klantportaal.",
  },
  {
    icon: Truck,
    title: "Snelle levering",
    body: "Grote voorraad en dagelijkse uitgaande zendingen. Bestelt u voor 15:00? Dan gaat het vaak dezelfde dag nog mee.",
  },
  {
    icon: Headphones,
    title: "24/7 storingsdienst",
    body: "Voor zakelijke klanten met een onderhoudscontract is onze storingsdienst 24 uur per dag, 7 dagen per week bereikbaar.",
  },
];

const sectors = [
  { icon: Building2, label: "Aannemers & bouw" },
  { icon: Wrench, label: "Installateurs" },
  { icon: HardHat, label: "Projectontwikkelaars" },
  { icon: Zap, label: "Industrie & productie" },
  { icon: Users, label: "VvE's & verenigingen" },
  { icon: ShieldCheck, label: "Beveiligingsbedrijven" },
];

const relatedServices = [
  {
    icon: Wrench,
    title: "Elektrotechniek",
    body: "Van groepenkast tot complete winkelrenovatie: onze erkende monteurs installeren elektrotechniek, beveiliging en automatisering.",
    to: "/elektrotechniek",
  },
  {
    icon: BatteryCharging,
    title: "Laadpaal installateur",
    body: "Laadpalen voor thuis, zakelijk of VvE. Inclusief aanpassing groepenkast, kabelwerk en NEN1010-meetrapport.",
    to: "/laadpaal-installateur",
  },
  {
    icon: Camera,
    title: "Camerabewaking offerte",
    body: "Stel zelf een camerasysteem samen voor uw bedrijfspand of terrein. Inclusief montage door erkende monteurs.",
    to: "/offerte-aanvragen", product: "camerabewaking",
  },
  {
    icon: Zap,
    title: "Groepenkast offerte",
    body: "Configureer een nieuwe of vervangende groepenkast op maat. Van 1-fase tot 3-fase met laadgroep type B.",
    to: "/offerte-aanvragen", product: "groepenkast",
  },
  {
    icon: Users,
    title: "Advies op maat",
    body: "Persoonlijk advies over camerasystemen, groepenkasten, laadpalen en energieoplossingen voor uw organisatie.",
    to: "/advies",
  },
  {
    icon: Lightbulb,
    title: "Ons assortiment",
    body: "Bekijk het complete Elvion-assortiment: groepenkasten, LED, beveiliging, laadpalen, thuisbatterijen en netwerken.",
    to: "/assortiment",
  },
];

function ZakelijkPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-border">
          <div className="container mx-auto px-4 py-16 md:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white/80 backdrop-blur-sm">
                <Building2 className="h-4 w-4" /> Zakelijk
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight text-white md:text-6xl">
                B2B partner in elektrotechniek & beveiliging
              </h1>
              <p className="mt-4 text-lg text-white/70 md:text-xl">
                Elvion Group Nederland is de betrouwbare leverancier voor aannemers, installateurs en bedrijven. 
                Van inkoop tot installatie, wij denken met u mee.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/auth">
                  <Button className="group/btn">
                    Zakelijke account aanvragen
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                  </Button>
                </Link>
                <Link to="/offerte-aanvragen">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white">
                    Offerte opvragen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-b border-border bg-muted/30">
          <div className="container mx-auto px-4 py-10">
            <div className="mx-auto grid max-w-4xl grid-cols-2 gap-8 text-center md:grid-cols-4">
              <div>
                <div className="text-3xl font-bold text-foreground">500+</div>
                <div className="mt-1 text-sm text-muted-foreground">Zakelijke klanten</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">15+</div>
                <div className="mt-1 text-sm text-muted-foreground">Jaar ervaring</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">24/7</div>
                <div className="mt-1 text-sm text-muted-foreground">Storingsdienst</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">100%</div>
                <div className="mt-1 text-sm text-muted-foreground">NEN-gecertificeerd</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-16 md:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-foreground">Waarom zaken doen met Elvion?</h2>
            <p className="mt-3 text-muted-foreground">
              Wij begrijpen dat u als professional snelheid, betrouwbaarheid en scherpe prijzen nodig heeft.
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:shadow-md"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Sectors */}
        <section className="border-t border-border bg-muted/20">
          <div className="container mx-auto px-4 py-16 md:py-20">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-foreground">Voor wie?</h2>
              <p className="mt-3 text-muted-foreground">
                Wij leveren aan diverse sectoren binnen de gebouwde omgeving en industrie.
              </p>
            </div>
            <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {sectors.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-5 text-center transition-all hover:border-primary/30"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO content */}
        <section className="border-t border-border bg-muted/10">
          <div className="container mx-auto max-w-5xl px-4 py-16 md:py-20">
            <h2 className="text-3xl font-bold text-foreground">
              Zakelijke partner voor elektrotechniek en beveiliging in Nederland
            </h2>
            <div className="mt-5 space-y-4 text-muted-foreground">
              <p>
                Elvion Group Nederland is een landelijk werkend installatiebedrijf en
                groothandel voor zakelijke opdrachtgevers. Wij leveren en
                installeren elektrotechnische installaties, laadpalen,
                groepenkasten, camerabewaking, toegangscontrole,
                bekabeling en LED-verlichting bij aannemers, installateurs,
                projectontwikkelaars, VvE&apos;s, industriële bedrijven en
                beveiligingsdiensten. Van renovatie van een enkele
                meterkast tot volledige elektrotechnische inrichting van
                een bedrijfspand of nieuwbouwproject.
              </p>
              <p>
                Al onze werkzaamheden voeren wij uit conform NEN 1010 en
                NEN 3140. Voor grote projecten stellen wij een vaste
                accountmanager en projectleider aan die uw dossier van
                offerte tot oplevering begeleidt. Documentatie zoals
                meetrapporten, revisietekeningen en garantiebewijzen
                leveren wij digitaal aan via het klantportaal.
              </p>
              <p>
                Voor onderhoud en storingen sluit u een servicecontract
                af met vaste responstijden. Onze monteurs zijn
                gecertificeerd en werken vanuit meerdere regio&apos;s in
                Nederland, waardoor wij op korte termijn beschikbaar
                zijn voor service en projectwerk.
              </p>
            </div>

            <h3 className="mt-10 text-2xl font-bold text-foreground">
              Onze zakelijke diensten
            </h3>
            <ul className="mt-4 grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
              <li>&#8211; <Link to="/elektrotechniek" className="underline underline-offset-4 hover:text-primary">Aanleg en renovatie van elektrische installaties</Link></li>
              <li>&#8211; <Link to="/laadpaal-installateur" className="underline underline-offset-4 hover:text-primary">Installatie en onderhoud van laadpalen en laadpleinen</Link></li>
              <li>&#8211; <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="underline underline-offset-4 hover:text-primary">Groepenkasten en verdeelinrichtingen op maat</Link></li>
              <li>&#8211; <Link to="/offerte-aanvragen" search={{ product: "camerabewaking" }} className="underline underline-offset-4 hover:text-primary">Camerabewaking en toegangscontrole voor bedrijven</Link></li>
              <li>&#8211; <Link to="/assortiment" className="underline underline-offset-4 hover:text-primary">Data-, netwerk- en glasvezelbekabeling</Link></li>
              <li>&#8211; <Link to="/assortiment" className="underline underline-offset-4 hover:text-primary">LED-verlichting voor kantoor, magazijn en buitenterrein</Link></li>
              <li>&#8211; NEN 3140-inspecties en periodieke keuringen</li>
              <li>&#8211; Servicecontracten met 24-uurs storingsdienst</li>
            </ul>

            <h3 className="mt-10 text-2xl font-bold text-foreground">
              Voor welke sectoren werkt Elvion Group Nederland?
            </h3>
            <p className="mt-3 text-muted-foreground">
              Wij zijn actief in bouw en aanneming, installatietechniek,
              vastgoed en beheer, industrie en productie, logistiek,
              zorginstellingen, onderwijs, retail en overheden. Onze
              schaal maakt dat we kleine service-opdrachten en
              meerjarige raamovereenkomsten met dezelfde nauwkeurigheid
              uitvoeren.
            </p>

            <h3 className="mt-10 text-2xl font-bold text-foreground">
              Werkwijze
            </h3>
            <ol className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>1. Intake met uw accountmanager en, indien nodig, een schouw op locatie.</li>
              <li>2. Uitwerking van een technisch voorstel met offerte en planning.</li>
              <li>3. Uitvoering door gecertificeerde monteurs, inclusief tussentijdse rapportages.</li>
              <li>4. Oplevering met NEN-meetrapport, revisiedocumentatie en garantiebewijs.</li>
              <li>5. Optioneel servicecontract voor onderhoud en storingsopvolging.</li>
            </ol>

            <h3 className="mt-10 text-2xl font-bold text-foreground">
              Veelgestelde vragen zakelijk
            </h3>
            <div className="mt-4 space-y-5 text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground">Werkt Elvion Group Nederland in heel Nederland?</p>
                <p className="mt-1">Ja. Onze monteurs werken vanuit meerdere regio&apos;s en voeren projecten en serviceopdrachten in heel Nederland uit.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Kan ik een raamovereenkomst afsluiten?</p>
                <p className="mt-1">Ja. Voor terugkerende opdrachten stellen we een raamovereenkomst op met vaste tarieven, responstijden en servicecondities.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Zijn de installaties NEN-gecertificeerd?</p>
                <p className="mt-1">Alle werkzaamheden voeren wij uit conform NEN 1010 (nieuwe installaties) en NEN 3140 (periodieke inspectie van elektrische arbeidsmiddelen).</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Hoe snel kan een storing worden opgepakt?</p>
                <p className="mt-1">Klanten met een servicecontract profiteren van vaste responstijden, afhankelijk van het gekozen serviceniveau.</p>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    Meer weten of direct een afspraak maken?
                  </h3>
                  <p className="mt-2 text-muted-foreground">
                    Onze adviseurs denken graag met u mee over het beste ontwerp, de juiste materialen en een heldere planning.{" "}
                    <Link to="/klantenservice" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
                      Neem contact op
                    </Link>{" "}
                    voor vrijblijvend advies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gerelateerde diensten */}
        <section className="border-t border-border bg-muted/20">
          <div className="container mx-auto px-4 py-16 md:py-20">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-foreground">Gerelateerde diensten</h2>
              <p className="mt-3 text-muted-foreground">
                Ontdek onze specialistische pagina’s voor installatie, advies en productoffertes.
              </p>
            </div>
            <div className="mx-auto mt-12 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {relatedServices.map((s) => (
                <Link
                  key={s.title}
                  to={s.to}
                  search={"product" in s ? { product: s.product } : undefined}
                  className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:border-primary/30 hover:shadow-md"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-foreground group-hover:text-primary">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.body}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    Bekijk dienst
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 py-16 md:py-20">
          <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-8 text-center shadow-sm md:p-12">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange/10 text-orange">
              <Clock className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-foreground md:text-3xl">
              Start vandaag nog als zakelijke klant
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              Vraag een zakelijke account aan en ontvang binnen één werkdag toegang tot inkoopprijzen en uw persoonlijke accountmanager.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/auth">
                <Button className="group/btn">
                  Account aanvragen
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                </Button>
              </Link>
              <Link to="/klantenservice">
                <Button variant="outline">
                  Contact opnemen
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
